import { ReactNode, useState } from 'react';
import { Dropdown } from '@cimplico/components';
import { getChildrenByType, getChildrenProps } from '../../utilities/child-components';
import Author from './Author';
import Badge from './Badge';
import Message, { MessageProps } from './Message';
import RepliesToggle from './RepliesToggle';
import RepliesList, { RepliesListProps } from './RepliesList';
import QuickActions from './QuickActions';
import Reply from './Reply';
import NewReply from './NewReply';

type StructuredTextCardProps = {
    children: ReactNode | ReactNode[];
};

/**
 * Overall parent card component that wraps around other components
 */
function StructuredTextCard({ children }: StructuredTextCardProps): JSX.Element {
    const author = getChildrenByType(children, Author);
    const actions = getChildrenByType(children, Dropdown);
    const badge = getChildrenByType(children, Badge);
    const message = getChildrenByType(children, Message);
    const quickActions = getChildrenByType(children, QuickActions);
    const repliesList = getChildrenByType(children, RepliesList);

    const messageProps: MessageProps[] = getChildrenProps(message);
    const repliesListProps: RepliesListProps[] = getChildrenProps(repliesList);

    const [showingReplies, setShowingReplies] = useState(false);

    return (
        <div className="h-fit border rounded-md p-3 shadow-sm flex flex-col gap-2 bg-white relative">
            <div className="flex items-start justify-between">
                {author}
                {actions}
            </div>
            {badge}
            {message}
            {(quickActions.length > 0 || repliesList.length > 0) && (
                <div className="flex mt-auto items-center justify-between [&_*]:text-gray-600 [&_*]:font-normal">
                    {quickActions}
                    {messageProps[0]?.isEditing === false && (
                        <StructuredTextCard.RepliesToggle
                            canCreate={repliesListProps[0]?.canCreate || false}
                            replyCount={repliesListProps[0]?.children.length || 0}
                            enabled={showingReplies}
                            onToggle={(): void => setShowingReplies(!showingReplies)}
                        />
                    )}
                </div>
            )}
            {showingReplies && repliesList}
        </div>
    );
}

StructuredTextCard.Author = Author;
StructuredTextCard.Badge = Badge;
StructuredTextCard.Message = Message;
StructuredTextCard.NewReply = NewReply;
StructuredTextCard.QuickActions = QuickActions;
StructuredTextCard.RepliesList = RepliesList;
StructuredTextCard.RepliesToggle = RepliesToggle;
StructuredTextCard.Reply = Reply;

export default StructuredTextCard;
